"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Form as FormProvider } from "@/components/ui/form";
import { Button } from "@/components/ui/button.new";
import { H2 } from "@/components/ui/typography";
import { useTranslations } from "next-intl";
import { Cloud, CloudBackground } from "../../components";
import { useSignupForm } from "./use-signup-form";
import { FormField } from "./form-fields";
import { FormSection } from "./form-sections";
import { ConfirmationModal } from "./confirmation-modal";
import { trackEvent } from "@/lib/analytics/posthog";

// ============================================================================
// Types
// ============================================================================

type Country = {
  id: number;
  name: string;
};
type OnSubmitCallback = (formData: FormData) => Promise<{
  errors?: string[];
  success?: boolean;
  companyName?: string;
  email?: string;
  websiteDomain?: string;
}>;
type Props = Readonly<{
  onSubmit: OnSubmitCallback;
  countries: Country[];
}>;

// ============================================================================
// Container Components
// ============================================================================

const SubmitButton = t0 => {
  const $ = _c(9);
  const {
    isSubmitting
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== isSubmitting) {
    t1 = isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />;
    $[0] = isSubmitting;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isSubmitting || $[3] !== t) {
    t2 = isSubmitting ? t("loadingButton") : t("button");
    $[2] = isSubmitting;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isSubmitting || $[6] !== t1 || $[7] !== t2) {
    t3 = <Button type="submit" form="signup-form" disabled={isSubmitting} purpose="primary" shape="pill" className="w-full">{t1}{t2}</Button>;
    $[5] = isSubmitting;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const Container = t0 => {
  const $ = _c(16);
  const {
    children,
    isSubmitting
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CloudBackground><Cloud id="signup-cloud-1" size="large" position="left" className="top-1/4 left-2/3 h-[32rem] w-[32rem] -translate-x-1/2 opacity-15" /></CloudBackground>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <H2>{t2}</H2>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("description");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p>{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isSubmitting) {
    t6 = <SubmitButton isSubmitting={isSubmitting} />;
    $[9] = isSubmitting;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children || $[12] !== t3 || $[13] !== t5 || $[14] !== t6) {
    t7 = <div id="signup-form-container" data-testid="signup-form-container" className="relative space-y-4 overflow-hidden rounded-lg bg-white p-10">{t1}<div className="relative z-10 space-y-4">{t3}{t5}{children}{t6}</div></div>;
    $[11] = children;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};

// ============================================================================
// Main Form Component
// ============================================================================

export function Form({
  onSubmit,
  countries
}: Props) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    companyName: string;
    email: string;
    websiteDomain?: string;
  } | null>(null);
  const handleFormSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(formData);
      if (result.success && result.companyName && result.email) {
        // Track create_company event when company is successfully created
        trackEvent("create_company");
        setConfirmationData({
          companyName: result.companyName,
          email: result.email,
          websiteDomain: result.websiteDomain
        });
        setShowConfirmation(true);
      }
      return result;
    } finally {
      setIsSubmitting(false);
    }
  };
  const {
    form,
    handleSubmit
  } = useSignupForm({
    onSubmit: handleFormSubmit
  });
  const handleCloseModal = () => {
    setShowConfirmation(false);
    if (confirmationData?.websiteDomain) {
      window.location.href = confirmationData.websiteDomain;
    }
  };
  return <>
      <Container isSubmitting={isSubmitting}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit} id="signup-form" className="space-y-6">
            <input type="hidden" name="isSubmitting" value={isSubmitting.toString()} />
            <FormSection.PersonalDetails firstName={<FormField.FirstName disabled={isSubmitting} />} lastName={<FormField.LastName disabled={isSubmitting} />} email={<FormField.Email disabled={isSubmitting} />} contactNumber={<FormField.ContactNumber disabled={isSubmitting} />} />
            <FormSection.BusinessDetails businessName={<FormField.BusinessName disabled={isSubmitting} />} website={<FormField.Website disabled={isSubmitting} />} street={<FormField.Street disabled={isSubmitting} />} city={<FormField.City disabled={isSubmitting} />} zipCode={<FormField.ZipCode disabled={isSubmitting} />} country={<FormField.Country countries={countries} disabled={isSubmitting} />} />
          </form>
        </FormProvider>
      </Container>

      {confirmationData && <ConfirmationModal isOpen={showConfirmation} onClose={handleCloseModal} companyName={confirmationData.companyName} email={confirmationData.email} websiteDomain={confirmationData.websiteDomain} />}
    </>;
}